/**
 * Générateur — Analyser un programme simple donné et modifier ses paramètres.
 * Attendu ID : 1001 | Niveau : 5ème | Domaine : La pensée informatique
 *
 * Libellé officiel (spec 1001, 2026-09-21) : « Analyser un programme simple
 * donné et modifier ses paramètres. »
 *
 * Plan de cartes (mise en conformité spécification, 2026-09-22) :
 *
 *   Carte « deplacement » — Le lutin sur le quadrillage (niveaux 1 à 3).
 *   Un programme de déplacement donné, non exécutable, et un quadrillage
 *   centré sur l'origine où sont marqués la case de départ, l'orientation du
 *   lutin et les cases CIBLES. Chaque item porte les DEUX temps que le
 *   libellé lie par « et » : prévoir la suite des cases atteintes (analyser),
 *   puis dire quelle valeur donner au paramètre pour que le programme marque
 *   les cases cibles (modifier). Deux formats : QCM (une proposition = un
 *   trajet ET un réglage, niveaux 1 et 2) et texte à trous en glisser-déposer
 *   (un trou par case atteinte, puis un trou par paramètre, niveaux 1 à 3).
 *
 *   Carte « calcul » — Le programme de calcul (niveau 4). Une suite
 *   d'affectations et d'affichages, sans support visuel : l'élève prévoit les
 *   valeurs affichées, puis donne la valeur à mettre dans une ou deux données
 *   pour que le programme affiche la sortie cible. Texte à trous en saisie.
 *
 *   Cartes « allera » et « rect » (version précédente) : SUPPRIMÉES. Elles
 *   faisaient ÉCRIRE un programme (compléter les quatre « aller à » d'un carré,
 *   réécrire un script en « avancer / tourner »), ce que la spec exclut
 *   (« Aucune écriture de programme par l'élève : le programme est donné »),
 *   et leur seule question de modification (« quels blocs faut-il modifier ? »)
 *   ne demandait aucune valeur et n'était jamais validée par l'exécution du
 *   programme modifié. Aucun des deux temps n'était produit au complet.
 *
 * Spécification appliquée :
 *
 *   Bornes : 5 à 9 instructions (programme de déplacement), 6 à 8
 *   (programme de calcul) ; 1 ou 2 paramètres modifiables ; coordonnées
 *   entières relatives multiples du pas du quadrillage (30) dans
 *   [−120 ; 120] ; longueur d'un déplacement de 1 à 5 cases ; angle de
 *   rotation 90 ou 180 degrés, à droite ou à gauche ; 2 à 4 sorties à
 *   relever ; au moins un relevé différent entre la sortie initiale et la
 *   sortie cible (ici : tous) ; nombre de jeux de paramètres atteignant la
 *   cible calculé par exploration exhaustive de l'espace autorisé.
 *   Exclusions : aucune modification de structure, aucune écriture de
 *   programme, aucune boucle ni test, aucun jeu de paramètres autorisé qui
 *   ferait sortir le mobile du quadrillage (les domaines sont réduits à la
 *   génération puis ANNONCÉS dans l'énoncé), aucune cible non atteignable
 *   (la cible est une exécution du programme, et l'unicité du jeu de
 *   paramètres qui l'atteint est vérifiée), aucune exécution pendant
 *   l'analyse (l'énoncé le dit, l'app n'exécute rien).
 *
 *   Erreurs visées → mots-clés (_meta.params.distracteurs[].code) :
 *     1. supposer l'orientation de départ au lieu de la lire
 *                                              → orientation_supposee
 *     2. traiter une rotation comme une orientation absolue
 *                                              → rotation_absolue
 *     3. lire une coordonnée négative comme positive
 *                                              → coordonnee_negative_lue_positive
 *     4. modifier la structure au lieu des paramètres
 *                                              → modification_de_structure
 *     5. modifier au hasard sans analyser (le réglage qui ne place bien que
 *        la dernière case)                     → essai_sans_analyse
 *     6. modifier le bon paramètre dans le mauvais sens
 *                                              → mauvais_sens_de_la_modification
 *
 *   Graduation → cartes :
 *     niveau 1 (5 instructions, orientation droite, 1 longueur, 2 relevés)
 *                                              : deplacement (QCM et trous)
 *     niveau 2 (7 instructions, orientation tirée, 1 coordonnée, 3 relevés)
 *                                              : deplacement (QCM et trous)
 *     niveau 3 (9 instructions, rotations des deux sens dont un demi-tour,
 *               1 longueur et 1 angle, 4 relevés) : deplacement (trous)
 *     niveau 4 (programme de calcul, 2 ou 3 sorties, 1 ou 2 données)
 *                                              : calcul (trous en saisie)
 *
 *   Non tranché (voir audit/5eme/1001.md) : le type_reponse prescrit un
 *   éditeur de blocs verrouillé, une validation qui exécute le programme
 *   modifié et une exécution animée après coup — l'app n'a ni éditeur ni
 *   exécution ; la structure ne peut pas être changée puisqu'elle n'est pas
 *   éditable, et la validation porte sur les valeurs de paramètres, dont
 *   l'unicité est établie à la génération.
 */

import {
    randomChoice, randInt, shuffle, makeQcm, makeTexteTrou, filtrerDistracteurs,
} from '../../../utils';
import { imgElement, svgScript, MOINS } from './svgScratch';

const ATTENDU_ID = 1001;

const L_DEPLACEMENT = 'Le lutin sur le quadrillage';
const L_CALCUL = 'Le programme de calcul';

const opts = (label) => ({ niveau: '5eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Bornes de la spécification ──────────────────────────────

const PAS = 30;                                   // pas du quadrillage (divise toutes les coordonnées)
const COORD_MAX = 120;                            // valeur_d_une_coordonnee : [−120 ; 120]
const INDEX_MAX = COORD_MAX / PAS;                // cases −4 … 4 sur chaque axe
const LONGUEUR_MIN = 1, LONGUEUR_MAX = 5;         // longueur_d_un_deplacement
const ANGLES = [90, 180];                         // angle_d_une_rotation
const COORDONNEES = Array.from({ length: 2 * INDEX_MAX + 1 }, (_, k) => (k - INDEX_MAX) * PAS);

// ─── Orientations et rotations ───────────────────────────────

const DIRECTIONS = ['droite', 'haut', 'gauche', 'bas'];   // sens trigonométrique
const VECTEURS = { droite: [1, 0], haut: [0, 1], gauche: [-1, 0], bas: [0, -1] };
const VERS = { droite: 'vers la droite', haut: 'vers le haut', gauche: 'vers la gauche', bas: 'vers le bas' };

const tourner = (dir, sens, angle) => {
    const k = DIRECTIONS.indexOf(dir);
    if (angle === 180) return DIRECTIONS[(k + 2) % 4];
    return DIRECTIONS[(k + (sens === 'gauche' ? 1 : 3)) % 4];
};
/** Rotation lue comme une orientation absolue (erreur visée 2). */
const orientationAbsolue = (sens, angle) => (angle === 180 ? 'bas' : sens);

// ─── Écriture des nombres et des coordonnées ─────────────────

const fmtNb = (v) => (v < 0 ? `${MOINS}${Math.abs(v)}` : String(v));
const fmtCase = ([x, y]) => `(${fmtNb(x)} ; ${fmtNb(y)})`;
const listerCases = (cases) => cases.map(fmtCase).join(', ').replace(/, ([^,]*)$/, ' puis $1');

// ─── Programme de déplacement : exécution ────────────────────
//
// Instructions : { t: 'aller', x, y } | { t: 'orienter', dir }
//                | { t: 'avancer', n } | { t: 'tourner', sens, angle }
// Un relevé est produit par chaque « avancer » : la case atteinte.

const texteInstruction = (ins) => {
    if (ins.t === 'aller') return `aller à x: ${fmtNb(ins.x)} y: ${fmtNb(ins.y)}`;
    if (ins.t === 'orienter') return `s'orienter ${VERS[ins.dir]}`;
    if (ins.t === 'avancer') return `avancer de ${ins.n} ${ins.n > 1 ? 'cases' : 'case'}`;
    return `tourner à ${ins.sens} de ${ins.angle} degrés`;
};

const blocsProgramme = (prog) => [
    { t: 'hat', txt: 'quand le drapeau est cliqué' },
    ...prog.map((ins) => ({ t: 'move', txt: texteInstruction(ins) })),
];

/**
 * Exécute un programme de déplacement. `lecture` simule une erreur d'élève :
 * 'orientation_supposee' (le lutin est supposé orienté vers la droite),
 * 'rotation_absolue' (tourner à droite = s'orienter vers la droite),
 * 'coordonnee_negative_lue_positive' (les coordonnées de départ lues en positif).
 */
const executer = (prog, lecture = null) => {
    let dir = 'droite';
    let x = 0, y = 0;
    const releves = [];
    let valide = true;
    prog.forEach((ins) => {
        if (ins.t === 'aller') {
            x = lecture === 'coordonnee_negative_lue_positive' ? Math.abs(ins.x) : ins.x;
            y = lecture === 'coordonnee_negative_lue_positive' ? Math.abs(ins.y) : ins.y;
        } else if (ins.t === 'orienter') {
            dir = lecture === 'orientation_supposee' ? 'droite' : ins.dir;
        } else if (ins.t === 'tourner') {
            dir = lecture === 'rotation_absolue'
                ? orientationAbsolue(ins.sens, ins.angle)
                : tourner(dir, ins.sens, ins.angle);
        } else {
            const [dx, dy] = VECTEURS[dir];
            x += dx * ins.n * PAS;
            y += dy * ins.n * PAS;
            releves.push([x, y]);
            if (Math.abs(x) > COORD_MAX || Math.abs(y) > COORD_MAX) valide = false;
        }
    });
    return { releves, valide };
};

const memeSortie = (a, b) => a.length === b.length
    && a.every((c, k) => c[0] === b[k][0] && c[1] === b[k][1]);
const casesDistinctes = (releves) => new Set(releves.map((c) => `${c[0]}|${c[1]}`)).size === releves.length;

// ─── Paramètres modifiables et espace autorisé ───────────────

const appliquerCombo = (prog, params, combo) => {
    const copie = prog.map((ins) => ({ ...ins }));
    params.forEach((p, k) => { copie[p.instr][p.champ] = combo[k]; });
    return copie;
};

const combinaisons = (domaines) => domaines.reduce(
    (acc, valeurs) => acc.flatMap((c) => valeurs.map((v) => [...c, v])),
    [[]],
);

/**
 * Réduit les domaines des paramètres jusqu'à ce que TOUT jeu de paramètres
 * autorisé garde le lutin sur le quadrillage (exclusion de la spec) : les
 * bornes ainsi obtenues sont celles qui seront annoncées dans l'énoncé.
 * Rend null si un domaine devient trop petit pour que la recherche à
 * l'aveugle reste coûteuse.
 */
const stabiliserDomaines = (prog, params, minTailles) => {
    let domaines = params.map((p) => [...p.valeurs]);
    for (let tour = 0; tour < 10; tour++) {
        const courants = domaines;
        const avant = courants.map((d) => d.length).join(',');
        domaines = courants.map((valeurs, k) => valeurs.filter((v) => combinaisons(
            courants.map((d, j) => (j === k ? [v] : d)),
        ).every((combo) => executer(appliquerCombo(prog, params, combo)).valide)));
        if (domaines.some((d, k) => d.length < minTailles[k])) return null;
        if (domaines.map((d) => d.length).join(',') === avant) break;
    }
    // Domaines contigus : l'énoncé annonce des bornes, elles doivent être vraies.
    const contigu = domaines.every((d) => {
        const pas = d.length > 1 ? d[1] - d[0] : 1;
        return d.every((v, k) => k === 0 || v - d[k - 1] === pas);
    });
    return contigu ? domaines : null;
};

// ─── Construction d'un programme de déplacement par niveau ───

const tirerCoordonnee = () => randomChoice(COORDONNEES);
const tirerLongueur = () => randInt(LONGUEUR_MIN, LONGUEUR_MAX);

const squelette = (niveau) => {
    const depart = { t: 'aller', x: tirerCoordonnee(), y: tirerCoordonnee() };
    const orientation = { t: 'orienter', dir: niveau === 1 ? 'droite' : randomChoice(DIRECTIONS) };
    if (niveau === 1) {
        return [depart, orientation,
            { t: 'avancer', n: tirerLongueur() },
            { t: 'tourner', sens: randomChoice(['droite', 'gauche']), angle: 90 },
            { t: 'avancer', n: tirerLongueur() }];
    }
    if (niveau === 2) {
        return [depart, orientation,
            { t: 'avancer', n: tirerLongueur() },
            { t: 'tourner', sens: randomChoice(['droite', 'gauche']), angle: 90 },
            { t: 'avancer', n: tirerLongueur() },
            { t: 'tourner', sens: randomChoice(['droite', 'gauche']), angle: 90 },
            { t: 'avancer', n: tirerLongueur() }];
    }
    // niveau 3 : rotations à droite ET à gauche, dont un demi-tour
    const sens = shuffle(['droite', 'gauche', randomChoice(['droite', 'gauche'])]);
    const rangDemiTour = randInt(0, 2);
    const rotations = sens.map((s, k) => ({ t: 'tourner', sens: s, angle: k === rangDemiTour ? 180 : 90 }));
    return [depart, orientation,
        { t: 'avancer', n: tirerLongueur() }, rotations[0],
        { t: 'avancer', n: tirerLongueur() }, rotations[1],
        { t: 'avancer', n: tirerLongueur() }, rotations[2],
        { t: 'avancer', n: tirerLongueur() }];
};

/** Les paramètres modifiables du niveau, avec leur domaine brut. */
const parametresDe = (niveau, prog) => {
    if (niveau === 2) {
        const champ = randomChoice(['x', 'y']);
        return [{
            id: 'coordonnee', instr: 0, champ, valeurs: COORDONNEES, nature: 'coordonnée de départ',
            libelle: `la coordonnée ${champ} du bloc « aller à »`,
            bornes: (d) => `La coordonnée ${champ} du départ peut aller de ${fmtNb(d[0])} à ${fmtNb(d[d.length - 1])}.`,
            frag: (v) => `la coordonnée ${champ} du départ doit devenir ${v}`,
            court: (v) => `${champ} du départ à ${fmtNb(v)}`,
        }];
    }
    const iAvancer = prog.findIndex((ins) => ins.t === 'avancer');
    const longueur = {
        id: 'longueur', instr: iAvancer, champ: 'n', nature: 'longueur de déplacement',
        valeurs: Array.from({ length: LONGUEUR_MAX - LONGUEUR_MIN + 1 }, (_, k) => LONGUEUR_MIN + k),
        libelle: 'le premier bloc « avancer »',
        bornes: (d) => `Le premier bloc « avancer » peut aller de ${d[0]} à ${d[d.length - 1]} cases.`,
        frag: (v) => `le nombre de cases du premier bloc « avancer » doit devenir ${v}`,
        court: (v) => `premier « avancer » à ${v} ${v > 1 ? 'cases' : 'case'}`,
    };
    if (niveau !== 3) return [longueur];
    const iTourner = prog.findIndex((ins) => ins.t === 'tourner');
    return [longueur, {
        id: 'angle', instr: iTourner, champ: 'angle', valeurs: ANGLES, nature: 'angle de rotation',
        libelle: 'le premier bloc « tourner »',
        bornes: () => "L'angle du premier bloc « tourner » peut être 90 ou 180 degrés.",
        frag: (v) => `le premier bloc « tourner » doit devenir « tourner à ${prog[iTourner].sens} de ${v} degrés »`,
        court: (v) => `premier « tourner » à ${v} degrés`,
    }];
};

/**
 * Tire un programme de déplacement complet : squelette, domaines autorisés,
 * sortie initiale, sortie cible atteinte par un seul jeu de paramètres.
 * Boucle bornée, puis repli déterministe (niveau 1, tout est vérifié par
 * construction) : jamais de throw.
 */
const tirerDeplacement = (niveau, { minDistracteurs = 0, trajetObligatoire = false } = {}) => {
    for (let essai = 0; essai < 400; essai++) {
        const prog = squelette(niveau);
        const params = parametresDe(niveau, prog);
        const minTailles = params.map((p) => (p.id === 'angle' ? 2 : 4));
        const domaines = stabiliserDomaines(prog, params, minTailles);
        if (!domaines) continue;
        const initiales = params.map((p) => prog[p.instr][p.champ]);
        if (initiales.some((v, k) => !domaines[k].includes(v))) continue;

        const depart = executer(prog);
        if (!depart.valide || !casesDistinctes(depart.releves)) continue;

        const combos = combinaisons(domaines);
        const sorties = combos.map((c) => ({ combo: c, ...executer(appliquerCombo(prog, params, c)) }));
        // cibles possibles : tout jeu qui change TOUS les paramètres, dont
        // aucune case ne coïncide avec la sortie initiale (l'élève ne peut
        // rien recopier), atteint par un seul jeu de paramètres.
        const cibles = shuffle(sorties.filter((s) => s.valide
            && s.combo.every((v, k) => v !== initiales[k])
            && s.releves.every((c, k) => c[0] !== depart.releves[k][0] || c[1] !== depart.releves[k][1])
            && casesDistinctes(s.releves)
            && sorties.filter((u) => memeSortie(u.releves, s.releves)).length === 1));
        if (!cibles.length) continue;

        const cible = cibles[0];
        const t = {
            niveau, prog, params, domaines, initiales, combos: combos.length,
            releves: depart.releves, cible: cible.releves, comboCible: cible.combo,
            solutions: 1, sorties,
        };
        const distracteurs = distracteursDe(t);
        if (distracteurs.length < minDistracteurs) continue;
        if (trajetObligatoire && !distracteurs.some((d) => d.genre === 'trajet')) continue;
        return { ...t, distracteurs };
    }
    return deplacementDeSecours();
};

/** Repli déterministe, vérifié : départ en bas à gauche, deux déplacements. */
const deplacementDeSecours = () => {
    const prog = [
        { t: 'aller', x: -120, y: -120 }, { t: 'orienter', dir: 'droite' },
        { t: 'avancer', n: 2 }, { t: 'tourner', sens: 'gauche', angle: 90 },
        { t: 'avancer', n: 3 },
    ];
    const params = [parametresDe(1, prog)[0]];
    const domaines = [[1, 2, 3, 4, 5]];
    const initiales = [2];
    const combos = combinaisons(domaines);
    const sorties = combos.map((c) => ({ combo: c, ...executer(appliquerCombo(prog, params, c)) }));
    const cible = sorties.find((s) => s.combo[0] === 4);
    const t = {
        niveau: 1, prog, params, domaines, initiales, combos: combos.length,
        releves: executer(prog).releves, cible: cible.releves, comboCible: cible.combo,
        solutions: 1, sorties,
    };
    return { ...t, distracteurs: distracteursDe(t) };
};

// ─── Erreurs visées : trajets faux et réglages faux ──────────

const LECTURES_FAUSSES = [
    'orientation_supposee', 'rotation_absolue', 'coordonnee_negative_lue_positive',
];

const distracteursDe = (t) => {
    const out = [];
    const vus = new Set([t.releves.map(fmtCase).join('|')]);
    // trajets faux (erreurs 1, 2, 3) — jamais hors du quadrillage : une case
    // impossible se repère sans analyser, le distracteur serait perdu.
    LECTURES_FAUSSES.forEach((lecture) => {
        const r = executer(t.prog, lecture);
        const cle = r.releves.map(fmtCase).join('|');
        if (vus.has(cle) || !r.valide || memeSortie(r.releves, t.cible)) return;
        vus.add(cle);
        out.push({ code: lecture, genre: 'trajet', releves: r.releves });
    });

    // réglages faux (erreurs 4, 5, 6)
    const k = 0;                                   // le paramètre numérique principal
    const domaine = t.domaines[k];
    const v0 = t.initiales[k];
    const vCible = t.comboCible[k];
    const symetrique = v0 - (vCible - v0);
    const mauvaisSens = domaine.includes(symetrique) && symetrique !== vCible
        ? symetrique
        : domaine.filter((v) => v !== vCible && (v - v0) * (vCible - v0) <= 0)[0];
    if (mauvaisSens !== undefined) {
        out.push({
            code: 'mauvais_sens_de_la_modification', genre: 'reglage',
            valeurs: t.comboCible.map((v, j) => (j === k ? mauvaisSens : v)),
        });
    }
    // essai au hasard : le réglage qui ne place correctement que la DERNIÈRE
    // case (celui auquel un élève qui réexécute s'arrête), ou, quand il
    // n'existe pas, la valeur voisine de la bonne, du même côté.
    const derniere = t.cible[t.cible.length - 1];
    const bluff = t.sorties.find((s) => {
        const d = s.releves[s.releves.length - 1];
        return s.valide && d[0] === derniere[0] && d[1] === derniere[1] && !memeSortie(s.releves, t.cible);
    });
    if (bluff) out.push({ code: 'essai_sans_analyse', genre: 'reglage', valeurs: bluff.combo });
    else {
        const pas = domaine.length > 1 ? domaine[1] - domaine[0] : 1;
        const voisine = [vCible + pas, vCible - pas]
            .filter((v) => domaine.includes(v) && v !== v0 && v !== mauvaisSens)[0];
        if (voisine !== undefined) {
            out.push({
                code: 'essai_sans_analyse', genre: 'reglage',
                valeurs: t.comboCible.map((v, j) => (j === k ? voisine : v)),
            });
        }
    }
    // ajouter un bloc au lieu de changer une valeur
    out.push({ code: 'modification_de_structure', genre: 'structure' });
    return out;
};

// ─── Énoncés de la carte « deplacement » ─────────────────────

/** Réglage court, pour les propositions du QCM. */
const reglageCourt = (t, valeurs) => t.params.map((p, k) => p.court(valeurs[k])).join(' et ');

const texteStructure = (t) => {
    const n = t.prog.find((ins) => ins.t === 'avancer').n;
    return `un bloc « avancer de ${n} ${n > 1 ? 'cases' : 'case'} » ajouté à la fin`;
};

const enonceCommun = (t) => "<p><b>Ce programme n'est pas exécuté.</b> Le lutin marque la case "
    + 'où il arrive après chaque déplacement.</p>'
    + `<p>On veut qu'il marque, dans l'ordre, les cases ${listerCases(t.cible)}. `
    + 'Seules les valeurs des paramètres changent, le reste du programme ne change pas.</p>'
    + `<p>${t.params.map((p, k) => p.bornes(t.domaines[k])).join(' ')}</p>`;

const imagesDeplacement = (t) => ({
    version: 1,
    elements: [
        imgElement(svgScript(blocsProgramme(t.prog))).elements[0],
        imgElement(svgQuadrillage(t), 'medium').elements[0],
    ],
});

const metaDeplacement = (t) => ({
    niveau: t.niveau,
    nature_du_programme: 'deplacement',
    nb_instructions: t.prog.length,
    nb_parametres: t.params.length,
    natures_parametres: t.params.map((p) => p.nature),
    nb_releves: t.releves.length,
    coordonnees: [t.prog[0].x, t.prog[0].y],
    orientation: t.prog[1].dir,
    longueurs: t.prog.filter((i) => i.t === 'avancer').map((i) => i.n),
    angles: t.prog.filter((i) => i.t === 'tourner').map((i) => i.angle),
    domaines: t.domaines,
    valeurs_initiales: t.initiales,
    valeurs_cibles: t.comboCible,
    releves: t.releves.map(fmtCase),
    cible: t.cible.map(fmtCase),
    distance_a_la_cible: t.releves.filter((c, k) => c[0] !== t.cible[k][0] || c[1] !== t.cible[k][1]).length,
    nb_jeux_solutions: t.solutions,
    espace_autorise: t.combos,
});

// ═════════ Carte « deplacement » — QCM (niveaux 1 et 2) ═════════

const propositionQcm = (releves, reglage) => `Cases marquées ${listerCases(releves)}, `
    + `et ${reglage}.`;

const genDeplacementQcm = () => {
    const t = tirerDeplacement(randInt(1, 2), { minDistracteurs: 3, trajetObligatoire: true });
    const reglageJuste = reglageCourt(t, t.comboCible);
    const bonne = propositionQcm(t.releves, reglageJuste);
    const candidats = t.distracteurs.map((d) => {
        if (d.genre === 'trajet') return { code: d.code, genre: d.genre, texte: propositionQcm(d.releves, reglageJuste) };
        if (d.genre === 'structure') return { code: d.code, genre: d.genre, texte: propositionQcm(t.releves, texteStructure(t)) };
        return { code: d.code, genre: d.genre, texte: propositionQcm(t.releves, reglageCourt(t, d.valeurs)) };
    });
    // Un distracteur de chaque espèce au moins : sans trajet faux, l'analyse
    // ne départage rien ; sans réglage faux, la modification non plus.
    const vus = new Set([bonne]);
    const retenus = [];
    const prendre = (liste) => liste.forEach((c) => {
        if (vus.has(c.texte) || retenus.length >= 3) return;
        vus.add(c.texte);
        retenus.push(c);
    });
    const trajets = shuffle(candidats.filter((c) => c.genre === 'trajet'));
    const reglages = shuffle(candidats.filter((c) => c.genre !== 'trajet'));
    prendre(trajets.slice(0, 1));
    prendre(reglages.slice(0, 1));
    prendre(shuffle(candidats));
    const payload = makeQcm([bonne, ...retenus.map((c) => c.texte)], bonne, enonceCommun(t)
        + '<p><b>Quelles cases le programme marque-t-il, et que faut-il changer pour marquer '
        + 'les cases cibles ?</b></p>', opts(L_DEPLACEMENT));
    payload.elements_additionnels = imagesDeplacement(t);
    payload._meta.params = {
        ...metaDeplacement(t),
        distracteurs: retenus.map((c) => ({ code: c.code, texte: c.texte })),
    };
    return payload;
};

// ═════════ Carte « deplacement » — trous (niveaux 1 à 3) ═════════

const genDeplacementTrous = () => {
    const t = tirerDeplacement(randInt(1, 3));
    const analyse = t.releves.map((c, k) => (k === 0
        ? `Le lutin marque d'abord la case {{${fmtCase(c)}}}`
        : `, puis la case {{${fmtCase(c)}}}`)).join('') + '.';
    const reglage = t.params.map((p, k) => p.frag(
        p.id === 'coordonnee' ? `{{${fmtNb(t.comboCible[k])}}}` : `{{${t.comboCible[k]}}}`,
    )).join(' et ');
    const phrase = `${analyse} Pour marquer les cases cibles, ${reglage}.`;

    // Deux stocks séparés : des cases (pour les trous d'analyse) et des
    // valeurs de réglage (pour les trous de modification). Sans les secondes,
    // le trou de réglage n'aurait qu'une étiquette possible.
    const cases = [];
    const reglages = [];
    // les cases CIBLES, que l'énoncé donne : les recopier au lieu d'analyser
    // est la réponse à écarter (règle d'anti-recopie, pas une erreur visée).
    t.cible.forEach((c) => cases.push({ code: 'sortie_cible_recopiee', mot: fmtCase(c) }));
    t.distracteurs.forEach((d) => {
        if (d.genre === 'trajet') d.releves.forEach((c) => cases.push({ code: d.code, mot: fmtCase(c) }));
        if (d.genre === 'reglage') {
            d.valeurs.forEach((v, k) => {
                if (v === t.comboCible[k]) return;
                reglages.push({ code: d.code, mot: t.params[k].id === 'coordonnee' ? fmtNb(v) : String(v) });
            });
        }
    });
    // valeurs du domaine restées libres : des étiquettes plausibles, jamais du bruit
    t.domaines.forEach((domaine, k) => domaine.forEach((v) => {
        if (v === t.comboCible[k]) return;
        reglages.push({ code: 'valeur_du_domaine', mot: t.params[k].id === 'coordonnee' ? fmtNb(v) : String(v) });
    }));

    const vus = new Set();
    const unique = (liste) => liste.filter((c) => {
        if (vus.has(c.mot)) return false;
        vus.add(c.mot);
        return true;
    });
    const retenus = [
        ...unique(shuffle(cases)).slice(0, 3),
        ...unique(reglages.filter((c) => c.code !== 'valeur_du_domaine')).slice(0, 2),
        ...unique(shuffle(reglages)).slice(0, 2),
    ];
    const distracteurs = filtrerDistracteurs(phrase, retenus.map((c) => c.mot));

    const payload = makeTexteTrou(phrase, enonceCommun(t)
        + '<p><b>Complète : les cases que le programme marque, puis le réglage qui fait marquer '
        + 'les cases cibles.</b></p>', {
        ...opts(L_DEPLACEMENT), mode: 'drag', distracteurs,
    });
    payload.elements_additionnels = imagesDeplacement(t);
    payload._meta.params = {
        ...metaDeplacement(t),
        distracteurs: retenus.filter((c) => distracteurs.includes(c.mot) && c.code !== 'valeur_du_domaine')
            .map((c) => ({ code: c.code, mot: c.mot })),
    };
    return payload;
};

// ─── Quadrillage (SVG d'énoncé) ──────────────────────────────

const CELL = 26;

const svgQuadrillage = (t) => {
    const n = 2 * INDEX_MAX + 1;
    const mG = 34, mB = 40, mH = 8, mD = 8;   // mB : place pour les graduations PUIS la légende
    const W = mG + n * CELL + mD;
    const H = mH + n * CELL + mB;
    const px = (x) => mG + (x / PAS + INDEX_MAX) * CELL + CELL / 2;
    const py = (y) => mH + (INDEX_MAX - y / PAS) * CELL + CELL / 2;

    let frag = `<rect x="0" y="0" width="${W}" height="${H}" fill="#f8fafc"/>`;
    for (let k = 0; k <= n; k++) {
        const x = mG + k * CELL, y = mH + k * CELL;
        frag += `<line x1="${x}" y1="${mH}" x2="${x}" y2="${mH + n * CELL}" stroke="#cbd5e1" stroke-width="1"/>`
            + `<line x1="${mG}" y1="${y}" x2="${mG + n * CELL}" y2="${y}" stroke="#cbd5e1" stroke-width="1"/>`;
    }
    frag += `<line x1="${px(0)}" y1="${mH}" x2="${px(0)}" y2="${mH + n * CELL}" stroke="#64748b" stroke-width="1.5"/>`
        + `<line x1="${mG}" y1="${py(0)}" x2="${mG + n * CELL}" y2="${py(0)}" stroke="#64748b" stroke-width="1.5"/>`;
    COORDONNEES.filter((v) => v % 60 === 0).forEach((v) => {
        frag += `<text x="${px(v)}" y="${mH + n * CELL + 14}" font-size="9" fill="#475569" text-anchor="middle" font-family="Arial">${fmtNb(v)}</text>`
            + `<text x="${mG - 4}" y="${py(v) + 3}" font-size="9" fill="#475569" text-anchor="end" font-family="Arial">${fmtNb(v)}</text>`;
    });

    // cases cibles, numérotées dans l'ordre
    t.cible.forEach(([x, y], k) => {
        frag += `<rect x="${px(x) - CELL / 2 + 2}" y="${py(y) - CELL / 2 + 2}" width="${CELL - 4}" height="${CELL - 4}" rx="4" fill="#fde68a" stroke="#b45309" stroke-width="1.5"/>`
            + `<text x="${px(x)}" y="${py(y) + 4}" font-size="11" fill="#7c2d12" text-anchor="middle" font-weight="bold" font-family="Arial">${k + 1}</text>`;
    });

    // case de départ et orientation du lutin
    const [x0, y0] = [t.prog[0].x, t.prog[0].y];
    const [dx, dy] = VECTEURS[t.prog[1].dir];
    const cx = px(x0), cy = py(y0);
    const r = CELL / 2 - 4;
    const pointe = [cx + dx * r, cy - dy * r];
    const gauche = [cx - dx * r + dy * r * 0.8, cy + dy * r + dx * r * 0.8];
    const droite = [cx - dx * r - dy * r * 0.8, cy + dy * r - dx * r * 0.8];
    frag += `<rect x="${cx - CELL / 2 + 2}" y="${cy - CELL / 2 + 2}" width="${CELL - 4}" height="${CELL - 4}" rx="4" fill="#dbeafe" stroke="#1E3A8A" stroke-width="1.5"/>`
        + `<polygon points="${pointe[0].toFixed(1)},${pointe[1].toFixed(1)} ${gauche[0].toFixed(1)},${gauche[1].toFixed(1)} ${droite[0].toFixed(1)},${droite[1].toFixed(1)}" fill="#1E3A8A"/>`;

    frag += `<text x="${mG}" y="${H - 6}" font-size="10" fill="#1E3A8A" font-family="Arial">▲ départ</text>`
        + `<text x="${mG + 70}" y="${H - 6}" font-size="10" fill="#b45309" font-family="Arial">■ cases cibles</text>`;
    return `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${W} ${H}" width="${W}" height="${H}" font-family="Arial">${frag}</svg>`;
};

// ═════════ Carte « calcul » — niveau 4 ═════════
//
// Programmes de calcul : suite d'affectations et d'affichages. Les données
// modifiables ont un domaine annoncé ; la sortie cible est la suite des
// valeurs affichées, atteinte par un seul jeu de données (vérifié par
// exploration exhaustive de l'espace autorisé).

const PROGRAMMES_CALCUL = [
    {
        id: 'panier',
        quoi: 'le prix à payer pour une commande',
        donnees: [
            { nom: 'prix', min: 2, max: 20 },
            { nom: 'nombre', min: 2, max: 9 },
            { nom: 'frais', min: 1, max: 10 },
        ],
        modifiables: ['prix', 'frais'],
        etapes: (d) => [
            { nom: 'total', expr: 'prix × nombre', valeur: d.prix * d.nombre, dire: true },
            { nom: 'prix final', expr: 'total + frais', valeur: d.prix * d.nombre + d.frais, dire: true },
        ],
    },
    {
        id: 'score',
        quoi: 'le score d\'une partie',
        donnees: [
            { nom: 'niveau', min: 2, max: 9 },
            { nom: 'bonus', min: 1, max: 12 },
        ],
        modifiables: ['niveau', 'bonus'],
        etapes: (d) => [
            { nom: 'points', expr: 'niveau × 10', valeur: d.niveau * 10, dire: true },
            { nom: 'score', expr: 'points + bonus', valeur: d.niveau * 10 + d.bonus, dire: true },
            { nom: 'score doublé', expr: 'score × 2', valeur: (d.niveau * 10 + d.bonus) * 2, dire: true },
        ],
    },
    {
        id: 'rectangle',
        quoi: 'le périmètre puis l\'aire d\'un rectangle',
        donnees: [
            { nom: 'longueur', min: 3, max: 15 },
            { nom: 'largeur', min: 2, max: 9 },
        ],
        modifiables: ['longueur', 'largeur'],
        etapes: (d) => [
            { nom: 'périmètre', expr: '(longueur + largeur) × 2', valeur: (d.longueur + d.largeur) * 2, dire: true },
            { nom: 'aire', expr: 'longueur × largeur', valeur: d.longueur * d.largeur, dire: true },
        ],
    },
];

const domaineDonnee = (d) => Array.from({ length: d.max - d.min + 1 }, (_, k) => d.min + k);

const blocsCalcul = (modele, valeurs) => [
    { t: 'hat', txt: 'quand le drapeau est cliqué' },
    ...modele.donnees.map((d) => ({ t: 'variable', txt: `mettre ${d.nom} à ${valeurs[d.nom]}` })),
    ...modele.etapes(valeurs).flatMap((e) => [
        { t: 'variable', txt: `mettre ${e.nom} à ${e.expr}` },
        ...(e.dire ? [{ t: 'looks', txt: `dire ${e.nom}` }] : []),
    ]),
];

const sortiesCalcul = (modele, valeurs) => modele.etapes(valeurs).filter((e) => e.dire).map((e) => e.valeur);

/** Tire un programme de calcul, ses données initiales et une sortie cible unique. */
const tirerCalcul = () => {
    for (let essai = 0; essai < 200; essai++) {
        const modele = randomChoice(PROGRAMMES_CALCUL);
        const valeurs = {};
        modele.donnees.forEach((d) => { valeurs[d.nom] = randInt(d.min, d.max); });
        const nbParams = randInt(1, Math.min(2, modele.modifiables.length));
        const noms = shuffle([...modele.modifiables]).slice(0, nbParams);
        const params = noms.map((nom) => modele.donnees.find((d) => d.nom === nom));
        const domaines = params.map(domaineDonnee);
        const initiales = params.map((p) => valeurs[p.nom]);
        const combos = combinaisons(domaines);
        const sortieDe = (combo) => {
            const v = { ...valeurs };
            params.forEach((p, k) => { v[p.nom] = combo[k]; });
            return sortiesCalcul(modele, v);
        };
        const initiale = sortiesCalcul(modele, valeurs);
        const candidats = shuffle(combos.filter((c) => c.every((v, k) => v !== initiales[k])))
            .map((c) => ({ combo: c, sortie: sortieDe(c) }))
            .filter((s) => s.sortie.every((v, k) => v !== initiale[k]));
        const cible = candidats.find((s) => combos
            .filter((c) => sortieDe(c).every((v, k) => v === s.sortie[k])).length === 1);
        if (!cible) continue;
        return {
            modele, valeurs, params, domaines, initiales, sortie: initiale,
            cible: cible.sortie, comboCible: cible.combo, combos: combos.length,
        };
    }
    // repli déterministe : le panier, prix modifiable
    const modele = PROGRAMMES_CALCUL[0];
    const valeurs = { prix: 5, nombre: 3, frais: 4 };
    const params = [modele.donnees[0]];
    return {
        modele, valeurs, params, domaines: [domaineDonnee(modele.donnees[0])], initiales: [5],
        sortie: sortiesCalcul(modele, valeurs), cible: sortiesCalcul(modele, { ...valeurs, prix: 8 }),
        comboCible: [8], combos: domaineDonnee(modele.donnees[0]).length,
    };
};

const genCalculTrous = () => {
    const t = tirerCalcul();
    const analyse = t.sortie.map((v, k) => (k === 0
        ? `Le programme affiche d'abord {{${v}}}`
        : `, puis {{${v}}}`)).join('') + '.';
    const reglage = t.params.map((p, k) => `mettre ${p.nom} à {{${t.comboCible[k]}}}`).join(' et ');
    const phrase = `${analyse} Pour obtenir la sortie cible, il faut ${reglage}.`;
    const bornes = `La donnée ${t.params.map((p, k) => `${p.nom} peut aller de ${t.domaines[k][0]} `
        + `à ${t.domaines[k][t.domaines[k].length - 1]}`).join(', la donnée ')}`;
    const texte = "<p><b>Ce programme n'est pas exécuté.</b> Il calcule "
        + `${t.modele.quoi}.</p>`
        + `<p>On veut qu'il affiche ${t.cible.join(' puis ')}. `
        + `Seules les données changent, le reste du programme ne change pas. ${bornes}.</p>`
        + '<p><b>Complète : les valeurs que le programme affiche, puis la donnée à changer.</b></p>';
    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_CALCUL), mode: 'saisie',
        elements_additionnels: imgElement(svgScript(blocsCalcul(t.modele, t.valeurs))),
    });
    payload._meta.params = {
        niveau: 4,
        nature_du_programme: 'calcul',
        programme: t.modele.id,
        nb_instructions: blocsCalcul(t.modele, t.valeurs).length - 1,
        nb_parametres: t.params.length,
        natures_parametres: t.params.map(() => 'valeur d\'une donnée numérique'),
        nb_releves: t.sortie.length,
        donnees: t.valeurs,
        domaines: t.domaines,
        valeurs_initiales: t.initiales,
        valeurs_cibles: t.comboCible,
        releves: t.sortie,
        cible: t.cible,
        distance_a_la_cible: t.sortie.filter((v, k) => v !== t.cible[k]).length,
        nb_jeux_solutions: 1,
        espace_autorise: t.combos,
    };
    return payload;
};

// ─── Contrat distracteurs (référence de revue) ───────────────

export const REGLES_DISTRACTEURS_QCM = {
    invariants_communs: [
        'chaque item porte les DEUX temps : prévoir la sortie du programme donné, puis dire quel '
        + 'réglage atteint la sortie cible',
        "aucune exécution n'est accessible : l'énoncé le dit, l'app n'exécute aucun programme",
        'les bornes annoncées dans l\'énoncé sont exactement les domaines autorisés, réduits à la '
        + 'génération pour qu\'aucun jeu de paramètres ne fasse sortir le lutin du quadrillage',
        'la sortie cible est atteinte par un seul jeu de paramètres (exploration exhaustive)',
    ],
    deplacement_qcm_unique: 'une proposition = un trajet ET un réglage ; distracteurs : trajet faux '
        + '(orientation_supposee, rotation_absolue, coordonnee_negative_lue_positive) avec le bon '
        + 'réglage, ou trajet juste avec un réglage faux (mauvais_sens_de_la_modification, '
        + 'essai_sans_analyse, modification_de_structure)',
    deplacement_texte_trou_drag: 'étiquettes : les cases des trajets faux, les valeurs de réglage '
        + 'fausses codées, et les valeurs restantes du domaine annoncé (jamais du bruit)',
    calcul_texte_trou_saisie: 'saisie, aucun distracteur : les valeurs affichées sont calculées par '
        + "l'élève et la donnée cible est déterminée par la sortie visée",
};

// ═════════ Assemblage ═════════

const GENERATORS = {
    deplacement_qcm_unique: genDeplacementQcm,
    deplacement_texte_trou_drag: genDeplacementTrous,
    calcul_texte_trou_saisie: genCalculTrous,
};

const FORMAT_LABELS = {
    qcm_unique: 'QCM', texte_trou_drag: 'Drag', texte_trou_saisie: 'Saisie trous',
};
const buildFormats = (prefix) =>
    Object.keys(GENERATORS).filter(k => k.startsWith(`${prefix}_`))
        .map(k => ({ key: k, label: FORMAT_LABELS[k.slice(prefix.length + 1)] || k }));

export const CATEGORIES = [
    { id: 'deplacement', label: L_DEPLACEMENT, formatsList: buildFormats('deplacement') },
    { id: 'calcul', label: L_CALCUL, formatsList: buildFormats('calcul') },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) key = typeExercice;
    else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(k => k.startsWith(typeExercice) || k.includes(typeExercice));
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else key = randomChoice(Object.keys(GENERATORS));
    const payload = GENERATORS[key]();
    payload._meta.generatorKey = key;
    return payload;
};
